import { api } from "@/lib/api";
import { instantFromWallClock, zonedParts } from "@/lib/time";

/**
 * Personal follow-ups (D-59, BL-007).
 *
 * ⚠️ **Every item this module can fetch belongs to the signed-in person.** Not because
 * anything here filters — nothing here does — but because the server's query filter and a
 * SQL Row-Level Security policy both admit only rows whose author is the caller. There is no
 * user id in any request below and no author on any response, and that is deliberate: a
 * client that could name whose list it wanted would be a client somebody would eventually
 * point at a colleague.
 *
 * ⚠️ Titles and notes are free text somebody typed while reading a request, so treat them as
 * PHI: they are rendered as text and never interpolated into markup, a URL, a `title`
 * attribute that gets logged, or an analytics event.
 */

/**
 * The request an item is about, when the server could still resolve it.
 *
 * ⚠️ Resolved server-side on every read under the ticket's own filter and RLS — never stored
 * on the item. So this going missing is a live permission answer, not stale data.
 */
export interface TodoTicket {
  /** Opaque. The only value a route or link is ever built on (D-4). */
  number: string;

  /** `#412` — what people quote, and never a route (D-35). */
  displayNumber: number;

  subject: string;
  status: string;
}

export interface Todo {
  id: string;
  title: string;
  notes: string | null;
  dueAtUtc: string | null;
  completedAtUtc: string | null;
  createdAtUtc: string;

  /**
   * The requests this follow-up names and the caller can still see, oldest link first (D-98).
   *
   * ⚠️ Empty covers both "this was never about a request" and "none of them are readable any
   * more" — `unavailableTicketCount` is what tells those apart.
   */
  tickets: TodoTicket[];

  /**
   * How many of this item's links the server could not resolve for this caller (D-98).
   *
   * ⚠️ This is the "linked request unavailable" state, now a count rather than the boolean
   * D-59 carried. It says links exist and how many, and nothing at all about the requests
   * behind them — the reader made every one of them, so they already know that much, and
   * without it a note naming three requests where two have gone out of reach silently reads as
   * a note about one.
   */
  unavailableTicketCount: number;
}

/**
 * The list, bucketed by the server in the reader's own timezone.
 *
 * ⚠️ The split is not recomputed here. "Today" is a calendar day in a place, and a desk
 * covering ~29 sites has people in more than one — a second definition in the browser would
 * disagree with the badge, which the server also counts.
 */
export interface TodoList {
  overdue: Todo[];
  today: Todo[];
  upcoming: Todo[];
  someday: Todo[];
  completed: Todo[];
  timeZoneId: string;
}

export interface TodoSummary {
  dueNow: number;
}

export interface TodoInput {
  title: string;
  notes: string | null;
  dueAtUtc: string | null;
}

export const TODO_MAX_TITLE = 200;
export const TODO_MAX_NOTES = 2000;

export const listTodos = () => api<TodoList>("/api/todos");

export const todoSummary = () => api<TodoSummary>("/api/todos/summary");

export const listTodosForTicket = (ticketNumber: string) =>
  api<Todo[]>(`/api/todos/for-ticket/${encodeURIComponent(ticketNumber)}`);

/** The most requests one follow-up may name (D-98) — mirrors `TodoItem.MaxLinkedTickets`. */
export const TODO_MAX_TICKETS = 20;

/**
 * @param ticketNumbers The OPAQUE numbers (D-4), none, one or several (D-98). The server
 * authorises the caller against EACH request before it will store any link, and one refusal
 * fails the whole call — a follow-up created against two of the three requests somebody
 * selected is a note that silently means something else.
 */
export const createTodo = (input: TodoInput & { ticketNumbers?: string[] }) =>
  api<Todo>("/api/todos", { method: "POST", body: input });

export const updateTodo = (id: string, input: TodoInput) =>
  api<Todo>(`/api/todos/${id}`, { method: "PUT", body: input });

export const completeTodo = (id: string) =>
  api<Todo>(`/api/todos/${id}/complete`, { method: "POST" });

export const reopenTodo = (id: string) => api<Todo>(`/api/todos/${id}/reopen`, { method: "POST" });

/**
 * Points an existing follow-up at a request, or moves it to a different one (D-90).
 *
 * ⚠️ The OPAQUE number, never the readable `#412` — that finds a request and does not address
 * one (D-33). The server runs the same resolution and the same `ticket.read` check against
 * that record as creation does, and answers 404 both for a request that does not exist and
 * for one this caller cannot read.
 */
export const linkTodoTicket = (id: string, ticketNumber: string) =>
  api<Todo>(`/api/todos/${id}/ticket`, { method: "POST", body: { ticketNumber } });

/**
 * Detaches ONE request, leaving the note and any other links in place (D-98).
 *
 * ⚠️ A POST rather than a DELETE carrying a body — a DELETE with one is legal and widely
 * mishandled, and the value is a ticket number, which in a URL would land in browser history
 * and every proxy log.
 *
 * ⚠️ Needs no authorization against the request it names: it can only ever remove, and the
 * server matches the number against this item's own links rather than looking a ticket up, so
 * an unknown number is a no-op rather than an existence oracle.
 */
export const unlinkTodoTicket = (id: string, ticketNumber: string) =>
  api<Todo>(`/api/todos/${id}/ticket/remove`, { method: "POST", body: { ticketNumber } });

/** Detaches every request, keeping the note. Its own call because it is a different intent. */
export const unlinkAllTodoTickets = (id: string) =>
  api<Todo>(`/api/todos/${id}/ticket`, { method: "DELETE" });

export const deleteTodo = (id: string) => api<void>(`/api/todos/${id}`, { method: "DELETE" });

// ---------------------------------------------------------------------------
// When
// ---------------------------------------------------------------------------

export interface DuePreset {
  key: string;
  label: string;

  /**
   * Null clears the date — "no date" is a real answer, not an absent one.
   *
   * ⚠️ Takes the reader's zone rather than reading the browser's. "Tomorrow at 9" has to mean
   * nine in the morning where the PERSON is, and the server buckets the result against the
   * same zone it sent down; resolving against the browser put a reminder three hours out for
   * anybody working away from their site, under a heading that said 9:00.
   */
  resolve: (now: Date, zone: string) => Date | null;
}

/**
 * The four presets, and there are deliberately four.
 *
 * ⚠️ These are the ones people actually use — the set Gmail, Front and Superhuman converged
 * on independently. A longer menu is not more capable, it is a decision to make every time
 * somebody wants to be reminded on Monday, and the cost of that decision is that they stop
 * setting reminders.
 *
 * ⚠️ Every one resolves against the READER'S clock and zone — the server's answer, not the
 * browser's — so "tomorrow at 9" is nine in the morning where the person is. The instant is
 * converted to UTC on the way out and back to their zone for display, the rule for every
 * timestamp in this product, and the one that matters most here: a reminder three hours early
 * is a reminder that trained somebody to ignore reminders.
 */
export const DUE_PRESETS: DuePreset[] = [
  {
    key: "later-today",
    label: "Later today",
    resolve: (now, zone) => {
      const p = zonedParts(now, zone);
      return instantFromWallClock(zone, p.year, p.month, p.day, p.hour + 3, 0);
    },
  },
  {
    key: "tomorrow",
    label: "Tomorrow, 9:00",
    resolve: (now, zone) => {
      const p = zonedParts(now, zone);
      return instantFromWallClock(zone, p.year, p.month, p.day + 1, 9, 0);
    },
  },
  {
    key: "monday",
    label: "Monday, 9:00",
    resolve: (now, zone) => {
      const p = zonedParts(now, zone);
      // Days until the NEXT Monday. On a Monday that is seven, not zero — somebody choosing
      // "Monday" on a Monday morning means next week, never four minutes ago.
      const daysAhead = (8 - p.weekday) % 7 || 7;
      return instantFromWallClock(zone, p.year, p.month, p.day + daysAhead, 9, 0);
    },
  },
  {
    key: "none",
    label: "No date",
    resolve: () => null,
  },
];

// The date helpers this module used to own — `toLocalInputValue`, `describeDue` and their
// private calendar arithmetic — now live in `@/lib/time`, which takes the zone as an argument
// rather than assuming the browser's. They were the reason this screen printed "Dates shown
// in <zone>" above rows rendered in a different one.
